/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Box } from '@atgfr8/atg-ui-tailwind';
import { CloseIcon } from '@atgfr8/icons';
import {
  caretAfterDigits,
  militaryTimeToDate,
  parseMilitaryTime,
  sanitizeTimeInput,
  timeIntegerToTimeString,
  timeStringToTimeInteger,
} from '@utils/dateFormat';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import {
  createEffect,
  createSignal,
  JSX,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';

function parseTimeToInteger(
  time: string,
  allowMidnight: boolean,
): number | null {
  const value = timeStringToTimeInteger(time);
  return value === 0 && !allowMidnight ? null : value;
}

type PropsT = {
  placeholder?: string;
  onChange?: (value: number | null) => void;
  value?: number | null;
  label?: string;
  error?: string | null;
  dataTestId?: string;
  class?: string;
  mobile?: boolean;
  disabled?: boolean;
  height?: string;
  floatLabel?: boolean;
  variant?: 'default' | 'form' | 'tt';
  allowManualEntry?: boolean;
  allowMidnight?: boolean;
};

export const IntegerTimeInput = (props: PropsT) => {
  let timeInput: HTMLInputElement | null;
  let fpInstance: flatpickr.Instance | null;
  const errorColor = '#d32f2f';
  const disabledBorderColor = '#bdbdbd';
  const [displayValue, setDisplayValue] = createSignal('');
  const [syncTick, setSyncTick] = createSignal(0);
  const allowManualEntry = () =>
    (props.allowManualEntry ?? false) && !(props.mobile ?? false);
  const isFormVariant = () => props.variant === 'form';
  const isTtVariant = () => props.variant === 'tt';

  const resyncFromValue = () => setSyncTick((tick) => tick + 1);

  const commitTimeChange = (value: number | null) => {
    if (value === (props.value ?? null)) {
      resyncFromValue();
      return;
    }
    props.onChange?.(value);
    resyncFromValue();
  };

  const applyTypedTime = (raw: string) => {
    const parsed = parseMilitaryTime(raw);
    if (parsed !== null) {
      commitTimeChange(
        parseTimeToInteger(parsed, props.allowMidnight ?? false),
      );
    } else if (raw.trim() === '') {
      commitTimeChange(null);
    } else {
      resyncFromValue();
    }
  };

  const setCaret = (el: HTMLInputElement, position: number) => {
    if (el.selectionStart === position && el.selectionEnd === position) return;
    el.setSelectionRange(position, position);
  };

  const handleTimeKeyDown: JSX.EventHandler<HTMLInputElement, KeyboardEvent> = (
    e,
  ) => {
    if (!allowManualEntry()) return;
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;

    const el = e.currentTarget;
    const caret = el.selectionStart ?? 0;
    if (caret !== el.selectionEnd) return;

    if (e.key === 'Backspace' && el.value[caret - 1] === ':') {
      setCaret(el, caret - 1);
    } else if (e.key === 'Delete' && el.value[caret] === ':') {
      setCaret(el, caret + 1);
    }
  };

  const handleTimeInput: JSX.EventHandler<HTMLInputElement, InputEvent> = (
    e,
  ) => {
    if (!allowManualEntry()) return;

    const el = e.currentTarget;
    const caret = el.selectionStart ?? el.value.length;
    const beforeCaret = el.value.slice(0, caret);
    const digitsBeforeCaret = beforeCaret.replace(/\D/g, '').length;
    const pastSeparator = beforeCaret.includes(':');

    const sanitized = sanitizeTimeInput(el.value);
    if (el.value !== sanitized) el.value = sanitized;
    setCaret(el, caretAfterDigits(sanitized, digitsBeforeCaret, pastSeparator));
    setDisplayValue(sanitized);
  };

  createEffect(() => {
    syncTick();
    const timeValue = timeIntegerToTimeString(props.value);
    setDisplayValue(timeValue);
    if (timeInput) timeInput.value = timeValue;
    if (timeValue) fpInstance?.setDate(timeValue, false);
    else fpInstance?.clear(false);
  });

  onMount(() => {
    if (timeInput) {
      fpInstance = flatpickr(timeInput, {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
        disableMobile: props.mobile ?? false,
        ...(allowManualEntry()
          ? { allowInput: true, parseDate: militaryTimeToDate }
          : {}),
        defaultDate: timeIntegerToTimeString(props.value ?? null),
        onChange: (selectedDates, dateStr) => {
          if (selectedDates.length === 0 || !dateStr) {
            if (allowManualEntry() && displayValue().trim() !== '') {
              resyncFromValue();
              return;
            }
            commitTimeChange(null);
            return;
          }
          commitTimeChange(
            parseTimeToInteger(dateStr, props.allowMidnight ?? false),
          );
        },
      });
    }
  });

  onCleanup(() => {
    if (fpInstance) {
      fpInstance.destroy();
    }
  });

  const handleClear = () => {
    commitTimeChange(null);
  };

  return (
    <Box class={`${props.floatLabel ? 'relative' : ''} ${props.class ?? ''}`}>
      <Box
        style={
          !!props.error
            ? { color: errorColor }
            : isFormVariant() || isTtVariant()
              ? {}
              : { color: '#123b50' }
        }
        class={`w-fit z-10 ${
          isFormVariant()
            ? 'text-sm font-medium text-armstrong-blue'
            : isTtVariant()
              ? 'text-sm font-medium text-armstrong-primary'
              : 'text-xs font-bold'
        } ${
          props.floatLabel
            ? 'text-shadow-thick absolute -top-2.5 left-2 px-1 pointer-events-none'
            : 'bg-white relative top-2 left-2 px-2'
        }`}
      >
        {props.label}
      </Box>
      <Box
        class={`rounded-md p-1 relative ${
          isTtVariant() ? 'border-2' : 'border'
        } ${
          props.disabled
            ? `pointer-events-none bg-gray-100 ${
                isFormVariant()
                  ? 'border-gray-200'
                  : isTtVariant()
                    ? 'opacity-60'
                    : ''
              }`
            : isFormVariant()
              ? 'bg-white border-gray-400 transition-colors hover:border-armstrong-blue focus-within:border-armstrong-blue focus-within:ring-1 focus-within:ring-armstrong-blue'
              : 'hover:border-black'
        }`}
        style={
          !!props.error
            ? { 'border-color': errorColor }
            : isFormVariant()
              ? {}
              : props.disabled && !isTtVariant()
                ? { 'border-color': disabledBorderColor }
                : { 'border-color': '#80b6cf' }
        }
        dataTestId={props.dataTestId}
      >
        <input
          class={`focus:outline-hidden w-full px-2 pr-8 disabled:bg-transparent disabled:text-gray-400 ${
            isFormVariant() ? 'placeholder:text-gray-400' : ''
          } ${props.height ?? 'h-6'}`}
          ref={(el) => (timeInput = el)}
          type={allowManualEntry() || props.mobile ? 'text' : 'time'}
          inputMode={allowManualEntry() ? 'numeric' : undefined}
          placeholder={props.placeholder}
          value={displayValue()}
          disabled={props.disabled}
          maxLength={allowManualEntry() ? 5 : undefined}
          onKeyDown={handleTimeKeyDown}
          onInput={handleTimeInput}
          onBlur={(e) => {
            if (!allowManualEntry()) return;
            applyTypedTime(e.currentTarget.value);
          }}
        />
        <Show
          when={
            props.value !== null && props.value !== undefined && !props.disabled
          }
        >
          <Box
            class="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer hover:opacity-70"
            onClick={handleClear}
          >
            <CloseIcon size={'18px'} color="#666" />
          </Box>
        </Show>
      </Box>
      <Show when={!!props.error}>
        <p class="text-[#d32f2f] text-xs pl-[1em]">{props.error}</p>
      </Show>
    </Box>
  );
};
